<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0">파일 업로드</h5>
              <a-button @click="$router.push('/storage')" type="link">
                <template #icon><arrow-left-outlined /></template>
                자료실로 돌아가기
              </a-button>
            </div>
          </div>
          <div class="card-body px-4 py-4">
            <a-form
              ref="uploadForm"
              :model="formState"
              layout="vertical"
            >
              <!-- 컨테이너 선택 -->
              <a-form-item 
                label="저장소 선택" 
                name="containerName"
              >
                <a-select
                  v-model:value="formState.containerName"
                  placeholder="저장소를 선택하세요"
                  class="container-select"
                >
                  <a-select-option 
                    v-for="container in containers" 
                    :key="container" 
                    :value="container"
                  >
                    {{ container }}
                  </a-select-option>
                </a-select>
              </a-form-item>
              
              <!-- 업로드 경로 (선택항목) -->
              <a-form-item 
                label="업로드 경로 (선택사항)" 
                name="uploadPath"
              >
                <div v-if="isPathFromNavigation" class="path-info mb-2">
                  <a-alert type="info" show-icon>
                    <template #message>
                      <span>현재 "<strong>{{ getDisplayPath(formState.uploadPath) }}</strong>" 폴더에 업로드됩니다.</span>
                    </template>
                  </a-alert>
                </div>
                <a-input
                  v-model:value="formState.uploadPath"
                  placeholder="업로드할 경로를 입력하세요"
                  allow-clear
                  @change="isPathFromNavigation = false"
                />
                <div class="hint-text">* 특정 경로에 저장하려면 입력하세요. 예) images/profile</div>
              </a-form-item>

              <a-form-item 
                label="파일 선택" 
                name="files"
              >
                <a-upload
                  v-model:file-list="fileList"
                  :multiple="true"
                  :show-upload-list="false"
                  :before-upload="beforeUpload"
                  @change="handleFileChange"
                >
                  <a-button>
                    <upload-outlined />
                    파일 선택
                  </a-button>
                </a-upload>

                <div class="file-list" v-if="fileList.length > 0">
                  <div v-for="(file, index) in fileList" :key="index" class="file-item">
                    <div class="file-info">
                      <span class="file-name">{{ file.name }}</span>
                      <span class="file-size">({{ formatFileSize(file.size) }})</span>
                    </div>
                    <close-outlined class="remove-file" @click="removeFile(index)" />
                  </div>
                </div>
              </a-form-item>

              <div class="form-footer">
                <a-button @click="$router.push('/storage')">취소</a-button>
                <a-button 
                  type="primary" 
                  :loading="loading"
                  :disabled="fileList.length === 0 || !formState.containerName"
                  @click="handleSubmit"
                  class="yellow-button ml-2"
                >업로드</a-button>
              </div>
            </a-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { message } from 'ant-design-vue';
import { UploadOutlined, CloseOutlined, ArrowLeftOutlined } from '@ant-design/icons-vue';

export default defineComponent({
  name: 'FileUpload',

  components: {
    UploadOutlined,
    CloseOutlined,
    ArrowLeftOutlined
  },

  data() {
    return {
      loading: false,
      fileList: [],
      formState: {
        uploadPath: '',
        containerName: ''
      },
      containers: ['certilife'], // 기본 컨테이너 목록
      isPathFromNavigation: false // 경로가 네비게이션에서 왔는지 여부
    }
  },

  created() {
    this.fetchContainers();
    
    // URL 쿼리 파라미터에서 경로와 컨테이너 정보 가져오기
    const pathParam = this.$route.query.path;
    const containerParam = this.$route.query.container;
    
    // 경로 설정
    if (pathParam && pathParam !== '/') {
      // 슬래시로 시작하면 제거
      this.formState.uploadPath = pathParam.startsWith('/') ? pathParam.substring(1) : pathParam;
      this.isPathFromNavigation = true;
    }
    
    // 컨테이너 설정
    if (containerParam) {
      this.formState.containerName = containerParam;
    }
  },

  methods: {
    // 컨테이너 목록 가져오기
    async fetchContainers() {
      try {
        const response = await this.$apiInstance.get('/object-storages/azure/status');
        if (response.data && response.data.success && response.data.data && response.data.data.containers) {
          this.containers = response.data.data.containers;
          
          // 기본 컨테이너가 있으면 선택
          if (response.data.data.defaultContainer) {
            this.formState.containerName = response.data.data.defaultContainer;
          } else if (this.containers.length > 0) {
            this.formState.containerName = this.containers[0];
          }
        }
      } catch (error) {
        console.error('컨테이너 목록 조회 실패:', error);
        message.error('저장소 목록을 불러오는데 실패했습니다.');
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    },

    beforeUpload(file) {
      // 파일 유형 검사
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf', 
                          'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                          'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          'text/plain', 'application/json', 'text/html', 'text/css', 'text/javascript']; 
      
      const fileExt = file.name.split('.').pop().toLowerCase();
      const allowedExts = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'json', 'html', 'css', 'js']; 
      
      // 확장자와 MIME 타입 둘 다 검사
      const isAllowedType = allowedTypes.includes(file.type) || allowedExts.includes(fileExt);
      if (!isAllowedType) {
        message.error(`${file.name} 파일 형식은 지원되지 않습니다.`);
        return false;
      }
      
      // 파일 크기 검사 (10MB로 제한)
      const isLessThan10M = file.size / 1024 / 1024 < 10;
      if (!isLessThan10M) {
        message.error(`${file.name} 파일이 10MB를 초과합니다.`);
        return false;
      }
      
      return false; // 수동 업로드를 위해 false 반환
    },

    handleFileChange(info) {
      const newFile = info.fileList[info.fileList.length - 1];
      if (newFile && !this.fileList.some(f => f.uid === newFile.uid)) {
        this.fileList.push(newFile);
      }
    },

    removeFile(index) {
      this.fileList.splice(index, 1);
    },

    async handleSubmit() {
      if (this.fileList.length === 0) {
        message.warning('파일을 선택해주세요.');
        return;
      }
      
      if (!this.formState.containerName) {
        message.warning('저장소를 선택해주세요.');
        return;
      }
      
      // 경로 형식 정리
      let uploadPath = '';
      if (this.formState.uploadPath) {
        uploadPath = this.formState.uploadPath.trim();
        
        // 경로 형식 검사 (특수문자 제한)
        const invalidChars = /[<>:"|?*]/g;
        if (invalidChars.test(uploadPath)) {
          message.error('업로드 경로에 다음 문자를 포함할 수 없습니다: < > : " | ? *');
          return;
        }
        
        // 경로 정규화 - 슬래시로 시작하는 경우 제거
        if (uploadPath.startsWith('/')) {
          uploadPath = uploadPath.substring(1);
        }
        
        // 슬래시로 끝나지 않고 경로가 있는 경우 추가
        if (uploadPath.length > 0 && !uploadPath.endsWith('/')) {
          uploadPath += '/';
        }
      }
      
      this.loading = true;
      
      try {
        // FormData 객체 생성
        const formData = new FormData();
        
        // 컨테이너 이름 추가
        formData.append('container', this.formState.containerName.trim());
        
        // 각 파일을 'files'라는 이름으로 FormData에 추가
        this.fileList.forEach((file) => {
          if (file.originFileObj && file.originFileObj instanceof File) {
            formData.append('files', file.originFileObj);
          }
        });
        
        // URL 구성
        const url = '/object-storages/azure/files/upload';
        const queryParams = new URLSearchParams();
        
        if (uploadPath) {
          queryParams.append('folderPath', uploadPath);
        }
        
        const fullUrl = `${url}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        
        // API 요청
        const response = await this.$apiInstance.post(fullUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Azure-Container': this.formState.containerName
          }
        });
        
        if (response.data.success) {
          message.success('파일이 성공적으로 업로드되었습니다.');
          // 리디렉션
          this.$router.push('/storage');
        } else {
          throw new Error(response.data.error || '업로드 실패');
        }
      } catch (error) {
        console.error('파일 업로드 실패:', error);
        message.error(`업로드 실패: ${error.message || '알 수 없는 오류'}`);
      } finally {
        this.loading = false;
      }
    },

    // 화면에 표시할 경로 가공
    getDisplayPath(path) {
      if (!path) return 'Root';
      
      // 마지막 슬래시 제거
      const cleanPath = path.endsWith('/') ? path.slice(0, -1) : path;
      
      // 경로 컴포넌트 분리
      const pathParts = cleanPath.split('/');
      if (pathParts.length === 0) return 'Root';
      
      // 마지막 폴더 이름 반환 또는 전체 경로
      if (pathParts.length === 1) return pathParts[0];
      
      // 아니면 전체 경로 반환
      return cleanPath;
    }
  }
});
</script>

<style lang="scss" scoped>
.container-select {
  width: 100%;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

.file-list {
  margin-top: 16px;
}

.file-item {
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:last-child {
    margin-bottom: 0;
  }

  .file-info {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .file-name {
    font-size: 14px;
    color: #000;
  }

  .file-size {
    font-size: 12px;
    color: #6b7280;
  }

  .remove-file {
    cursor: pointer;
    color: #6b7280;
    
    &:hover {
      color: #000;
    }
  }
}

:deep(.ant-upload) {
  .ant-upload-select {
    display: block;
  }

  .ant-btn {
    height: 48px;
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    
    &:hover {
      border-color: #000;
      color: #000;
    }

    .anticon {
      margin-right: 8px;
    }
  }
}

:deep(.ant-form-item-label) {
  padding: 0 0 8px;
  
  label {
    color: #000;
    font-size: 14px;
    height: auto;
    font-weight: 500;
  }
}

:deep(.ant-form-item-control-input) {
  .ant-input, .ant-select-selector {
    height: 48px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    
    &:hover, &:focus {
      border-color: #000;
      box-shadow: none;
    }
  }
  
  .ant-select-selector {
    display: flex;
    align-items: center;
    
    .ant-select-selection-item {
      line-height: normal;
    }
  }
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

.hint-text {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.ml-2 {
  margin-left: 8px;
}

.path-info {
  margin-bottom: 12px;
  
  :deep(.ant-alert) {
    border-radius: 8px;
    padding: 8px 12px;
  }
  
  :deep(.ant-alert-message) {
    color: #1890ff;
    font-size: 14px;
  }
  
  strong {
    color: #1890ff;
    font-weight: 600;
  }
}
</style> 